import { weenatIntl as wintl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import isString from 'lodash-es/isString';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { useClient } from '../../ClientProvider';
import { getPredicateKey } from '../../resources/alerts.types';
import useMutation from '../../useMutation';
import useQuery from '../../useQuery';
import { extractErrorsOnPhoneNumbersUpdate } from '../me';
import { isDevice, isPlot, isStation } from '../utils/resourcesTypeCheckers';
import { addNotificationMethodStep, buildPredicateWithNewOperation, computeInitialAcc, convertTargetsWithTzToTargets, getDisabledSources, isFrostAlert, isOperationWithoutParams, removeParamSetupStep, resetToDefaultSteps, simpleAlertCreationSteps } from './alerts';
import navReducer from './navReducer';
const initialNavigationState = {
    steps: simpleAlertCreationSteps,
    index: 0,
    history: []
};
const useAlertCreation = ({ initialTarget, addToast, onClose }) => {
    const initialTargetWithTZ = useMemo(() => {
        const plots = initialTarget != undefined && isPlot(initialTarget)
            ? [{ id: initialTarget.id, timezone: initialTarget.timezone }]
            : [];
        const devices = initialTarget != undefined && isDevice(initialTarget)
            ? [{ id: initialTarget.id, timezone: initialTarget.timezone }]
            : [];
        const stations = initialTarget != undefined && isStation(initialTarget)
            ? [{ id: initialTarget.id, timezone: initialTarget.timezone }]
            : [];
        return { devices, plots, stations };
    }, [initialTarget]);
    const client = useClient();
    const [navState, navDispatch] = useReducer(navReducer, initialNavigationState);
    const [targetsWithTZ, setTargetsWithTZ] = useState(initialTargetWithTZ);
    const [currentAlertCreationType, setCurrentAlertCreationType] = useState('simple');
    const [currentPredicates, setCurrentPredicates] = useState([
        {
            params: {}
        }
    ]);
    const [currentEditingPredicateIdx, setCurrentEditingPredicateIdx] = useState(0);
    const setPredicate = (p) => {
        currentPredicates[currentEditingPredicateIdx] = p;
        setCurrentPredicates([...currentPredicates]);
    };
    const currentEditingPredicate = currentPredicates[currentEditingPredicateIdx];
    const [alertValidationForm, setAlertValidationForm] = useState({});
    /**
     * Sources that have already been selected, or that cannot be because they're not part
     * of the initialTarget available measures
     */
    const disabledSources = useMemo(() => getDisabledSources({ predicates: currentPredicates, initialTarget }).filter((src) => currentEditingPredicate?.source !== src), [currentPredicates, currentEditingPredicate, initialTarget]);
    const myAccountRequest = useQuery(client.me.get());
    const myAccount = myAccountRequest.data;
    const userId = myAccount?.id;
    const profileRequest = useQuery(client.me.getMyProfile());
    const profile = profileRequest.data;
    const preferredUnits = profile ? profile.units : undefined;
    const allPhoneNumbersRequester = client.me.phoneNumbers.getAll;
    const allPhoneNumbersRequest = useQuery(allPhoneNumbersRequester());
    const allPhones = allPhoneNumbersRequest.data ?? [];
    const [updatePhones] = useMutation(client.me.phoneNumbers.updateAll(), {
        onSuccess: () => {
            navDispatch({ type: 'goToNextStep' });
        },
        onError: (e) => {
            const err = extractErrorsOnPhoneNumbersUpdate(e);
            if (!isNil(err)) {
                addToast(isString(err) ? err : err[0], { appearance: 'error' });
            }
        },
        keysToInvalidate: [allPhoneNumbersRequester().invalidationKey]
    });
    const alertCreationRequester = client.alerts.create(preferredUnits);
    const [createAlert, createAlertRequest] = useMutation(alertCreationRequester());
    const currentStepNumber = useMemo(() => navState.index + 1, [navState.index]);
    const currentStep = useMemo(() => navState.steps[navState.index], [navState.steps, navState.index]);
    const stepTitle = useMemo(() => wintl.t(`alerts.creationSteps.${currentStep}`, { capitalize: true }), [currentStep]);
    /** Resets alert, predicate, steps and nav history */
    const resetAll = useCallback((type) => {
        navDispatch({ type: 'reset', defaultSteps: resetToDefaultSteps(type) });
        setCurrentPredicates([{ params: {} }]);
        setCurrentEditingPredicateIdx(0);
    }, []);
    const handleAddPredicateForMultiParams = () => {
        setCurrentPredicates([...currentPredicates, { params: {} }]);
        setCurrentEditingPredicateIdx(currentPredicates.length);
        navDispatch({
            type: 'resetStepsAndNavTo',
            defaultSteps: resetToDefaultSteps(currentAlertCreationType),
            destination: 'pickPredicateSource'
        });
    };
    const handleEditPredicateForMultiParams = (idx) => {
        setCurrentEditingPredicateIdx(idx);
        navDispatch({
            type: 'resetStepsAndNavTo',
            defaultSteps: resetToDefaultSteps(currentAlertCreationType),
            destination: 'pickPredicateSource'
        });
    };
    const handleAlertCreation = async (finalAlert) => {
        const payload = await computeInitialAcc(finalAlert, targetsWithTZ, client);
        await createAlert(payload, {
            onSuccess: () => {
                onClose?.(() => addToast(wintl.t('actions.create_success'), { appearance: 'success' }));
            },
            onError: (e) => {
                addToast(`${wintl.t('alerts.creation_error')} ${e.message}`, { appearance: 'error' });
            }
        });
    };
    const handleTypeSelection = useCallback((type) => {
        if (!isNil(type)) {
            if (type !== currentAlertCreationType) {
                setCurrentAlertCreationType(type);
                resetAll(type);
            }
            navDispatch({ type: 'goToNextStep' });
        }
    }, [navDispatch, resetAll, setCurrentAlertCreationType, currentAlertCreationType]);
    const handleSourceSelection = useCallback((source) => {
        if (!isNil(source)) {
            if (currentEditingPredicate?.source !== source) {
                setPredicate({ params: {}, source });
                setTargetsWithTZ({ ...initialTargetWithTZ });
            }
            navDispatch({ type: 'goToNextStep' });
        }
    }, [currentEditingPredicate, setTargetsWithTZ, navDispatch, initialTargetWithTZ]);
    const handleOperationSelection = useCallback((op) => {
        if (!isNil(op)) {
            let newSteps = resetToDefaultSteps(currentAlertCreationType);
            const newPredicate = currentEditingPredicate
                ? buildPredicateWithNewOperation(currentEditingPredicate, op)
                : undefined;
            // skipping param step depending on the type of operations
            if (isOperationWithoutParams(op)) {
                newSteps = removeParamSetupStep(navState.steps);
            }
            if (newPredicate) {
                setPredicate(newPredicate);
            }
            const currentPredicates = {
                [getPredicateKey(currentEditingPredicate)]: currentEditingPredicate
            };
            if (!isFrostAlert(currentPredicates) && newSteps.includes('pickNotificationMethod')) {
                newSteps = newSteps.filter((step) => step !== 'pickNotificationMethod');
            }
            else if (isFrostAlert(currentPredicates)) {
                newSteps = addNotificationMethodStep(newSteps);
            }
            navDispatch({ type: 'updateStepsAndGoToNext', newSteps });
        }
    }, [
        currentEditingPredicate,
        navState,
        navDispatch,
        currentAlertCreationType,
        setCurrentPredicates,
        currentPredicates,
        removeParamSetupStep,
        resetToDefaultSteps
    ]);
    const handleParamsSelection = useCallback((params) => {
        if (!isNil(params) && currentEditingPredicate) {
            currentEditingPredicate.params = params;
            navDispatch({ type: 'goToNextStep' });
        }
    }, [currentEditingPredicate, navDispatch]);
    const handlePredicateDeletion = useCallback((idx) => {
        setCurrentEditingPredicateIdx(0);
        const isLastElement = currentPredicates.length === 1;
        if (isLastElement) {
            navDispatch({ type: 'navTo', destination: 'pickPredicateSource' });
            setCurrentPredicates([{ params: {} }]);
        }
        else {
            setCurrentPredicates((actualCurrentPredicates) => {
                const newCurrentPredicates = [...actualCurrentPredicates];
                newCurrentPredicates.splice(idx, 1);
                return newCurrentPredicates;
            });
        }
    }, [setCurrentEditingPredicateIdx, setCurrentPredicates, currentPredicates]);
    const handleMultiplePredicatesValidation = useCallback((predicates) => {
        if (!isNil(predicates)) {
            navDispatch({ type: 'goToNextStep' });
        }
    }, [navDispatch]);
    const handleTargetsSelection = useCallback((targetsWithTZ) => {
        if (!isNil(targetsWithTZ)) {
            // ensure that we don't add null bug seen in preprod
            targetsWithTZ.plots = targetsWithTZ.plots.filter((twTZ) => twTZ.id !== null);
            targetsWithTZ.devices = targetsWithTZ.devices.filter((twTZ) => twTZ.id !== null);
            setTargetsWithTZ(targetsWithTZ);
            navDispatch({ type: 'goToNextStep' });
        }
    }, [
        navState,
        currentPredicates,
        addNotificationMethodStep,
        isFrostAlert,
        navDispatch,
        setTargetsWithTZ
    ]);
    const handleNotificationMethodChoice = useCallback(({ isVoiceCallEnabled, telephoneNumber, telephoneCountryCode }) => {
        const currentPhone = !isEmpty(allPhones) ? allPhones.find((p) => p.isPrimary) : undefined;
        const hasPhoneBeenEdited = !isNil(telephoneNumber) &&
            !isNil(telephoneCountryCode) &&
            (telephoneNumber !== currentPhone?.number ||
                telephoneCountryCode !== currentPhone?.countryCode);
        if (!isNil(isVoiceCallEnabled)) {
            setAlertValidationForm({ ...alertValidationForm, isVoiceCallEnabled });
        }
        if (hasPhoneBeenEdited) {
            const newPrimary = !isNil(currentPhone)
                ? { ...currentPhone, countryCode: telephoneCountryCode, number: telephoneNumber }
                : {
                    isPrimary: true,
                    countryCode: telephoneCountryCode,
                    number: telephoneNumber,
                    isEnabled: true,
                    userId: userId
                };
            const newPhones = !isEmpty(allPhones)
                ? [...allPhones.filter((ph) => !ph.isPrimary), newPrimary]
                : [newPrimary];
            updatePhones(newPhones);
        }
        else {
            navDispatch({ type: 'goToNextStep' });
        }
    }, [updatePhones, alertValidationForm, setAlertValidationForm, allPhones]);
    const handleValidation = useCallback(async (alertValidationForm) => {
        if (!isNil(alertValidationForm)) {
            setAlertValidationForm(alertValidationForm);
            await handleAlertCreation({
                predicates: currentPredicates.reduce((acc, p) => {
                    if (p.source && p.op) {
                        const predicate = { source: p.source, op: p.op, params: p.params };
                        acc[getPredicateKey(predicate)] = predicate;
                    }
                    return acc;
                }, {}),
                targets: convertTargetsWithTzToTargets(targetsWithTZ),
                ...alertValidationForm
            });
        }
    }, [handleAlertCreation]);
    const handlePreviousOnValidateMultiplePredicates = useCallback(() => {
        if (currentAlertCreationType === 'simple') {
            navDispatch({ type: 'goToPreviousStep' });
        }
        else {
            navDispatch({
                type: 'resetHistoryAndNavTo',
                defaultSteps: resetToDefaultSteps(currentAlertCreationType),
                destination: 'pickAlertCreationType'
            });
        }
    }, [currentAlertCreationType, navDispatch]);
    return {
        alertValidationForm,
        createAlertRequest,
        currentAlertCreationType,
        currentPredicate: currentEditingPredicate,
        currentPredicates,
        currentStep,
        currentStepNumber,
        currentSteps: navState.steps,
        disabledSources,
        goToPreviousStep: () => navDispatch({
            type: 'goToPreviousStep'
        }),
        goToStep: (destination) => navDispatch({
            type: 'navTo',
            destination
        }),
        handleAddPredicateForMultiParams,
        handleEditPredicateForMultiParams,
        handleMultiplePredicatesValidation,
        handleNotificationMethodChoice,
        handleOperationSelection,
        handleParamsSelection,
        handlePredicateDeletion,
        handlePreviousOnValidateMultiplePredicates,
        handleSourceSelection,
        handleTargetsSelection,
        handleTypeSelection,
        handleValidation,
        setTargetsWithTZ,
        stepTitle,
        targetsWithTZ
    };
};
export default useAlertCreation;
